import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { Button } from '@/components/Button';
import {
  leadsAnalyticsService,
  FunnelResult,
  CampaignRow,
  TimingsResult,
} from '@/services/leads-analytics.service';
import {
  BarChart2, Trophy, Clock, Target, Download, ChevronUp, ChevronDown,
  ArrowUpRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';
import { Select } from '@/components/Select';

// ─── Date range presets ───────────────────────────────────────────────────────

interface DateRange { from?: string; to?: string; label: string }

const PRESETS: DateRange[] = [
  { label: 'Today',   from: todayIso(), to: todayIso() },
  { label: '7d',      from: daysAgoIso(7),  to: todayIso() },
  { label: '30d',     from: daysAgoIso(30), to: todayIso() },
  { label: '90d',     from: daysAgoIso(90), to: todayIso() },
  { label: 'All time' },
];

function todayIso() {
  return new Date().toISOString().split('T')[0];
}
function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSecs = Math.floor(ms / 1000);
  const totalMins = Math.floor(totalSecs / 60);
  const totalHours = Math.floor(totalMins / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const mins = totalMins % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function fmtNum(n: number) {
  return n.toLocaleString();
}

function fmtCurrency(n: number, currency: string) {
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ─── Funnel bar chart ─────────────────────────────────────────────────────────

const FUNNEL_COLORS: Record<string, string> = {
  New:       '#3b82f6', // blue
  Contacted: '#f59e0b', // amber
  Qualified: '#8b5cf6', // violet
  Won:       '#22c55e', // green
};

interface FunnelBarProps {
  funnel: FunnelResult;
}

function FunnelBars({ funnel }: FunnelBarProps) {
  const data = [
    { name: 'New',       value: funnel.new },
    { name: 'Contacted', value: funnel.contacted },
    { name: 'Qualified', value: funnel.qualified },
    { name: 'Won',       value: funnel.won },
  ];

  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className="space-y-3">
      {data.map((row, i) => {
        const prev = i > 0 ? data[i - 1].value : null;
        const dropOff = prev !== null && prev > 0
          ? Math.round(((prev - row.value) / prev) * 100)
          : null;
        const pct = Math.round((row.value / max) * 100);
        return (
          <div key={row.name} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ background: FUNNEL_COLORS[row.name] }}
                />
                <span className="font-semibold text-text-primary">{row.name}</span>
              </div>
              <div className="flex items-center gap-3">
                {dropOff !== null && (
                  <span className="text-danger text-[11px] font-medium">
                    -{dropOff}%
                  </span>
                )}
                <span className="tabular-nums font-bold text-text-primary">{fmtNum(row.value)}</span>
              </div>
            </div>
            <div className="h-2 w-full rounded-full bg-surface-alt overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: FUNNEL_COLORS[row.name] }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Campaign table ───────────────────────────────────────────────────────────

type SortKey = 'campaignName' | 'total' | 'qualified' | 'won' | 'dealValue';

function CampaignTable({ rows }: { rows: CampaignRow[] }) {
  const companyCurrency = useCompanyCurrency();
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...rows].sort((a, b) => {
    let va: string | number, vb: string | number;
    if (sortKey === 'campaignName') { va = a.campaignName; vb = b.campaignName; }
    else if (sortKey === 'total') { va = a.total; vb = b.total; }
    else if (sortKey === 'qualified') { va = a.byStatus.qualified; vb = b.byStatus.qualified; }
    else if (sortKey === 'won') { va = a.byStatus.won; vb = b.byStatus.won; }
    else { va = a.dealValue; vb = b.dealValue; }

    if (typeof va === 'string') {
      return sortDir === 'asc' ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
    }
    return sortDir === 'asc' ? (va - (vb as number)) : ((vb as number) - va);
  });

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k
      ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
      : <ChevronDown size={12} className="opacity-30" />;

  const ColHeader = ({ label, k }: { label: string; k: SortKey }) => (
    <button
      onClick={() => toggleSort(k)}
      className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted hover:text-text-primary"
    >
      {label}
      <SortIcon k={k} />
    </button>
  );

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary">
          <BarChart2 size={20} />
        </div>
        <p className="text-sm font-semibold text-text-primary">No campaign data yet</p>
        <p className="text-xs text-text-muted max-w-xs">
          Leads with a campaign attribution will appear here once they are tracked.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-alt">
            <th className="px-5 py-2.5 text-left"><ColHeader label="Campaign" k="campaignName" /></th>
            <th className="px-4 py-2.5 text-right"><ColHeader label="Total" k="total" /></th>
            <th className="px-4 py-2.5 text-right"><ColHeader label="Qualified" k="qualified" /></th>
            <th className="px-4 py-2.5 text-right"><ColHeader label="Won" k="won" /></th>
            <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Win rate</th>
            <th className="px-5 py-2.5 text-right"><ColHeader label="Deal value" k="dealValue" /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sorted.map((row) => {
            const winRate = row.total > 0
              ? Math.round((row.byStatus.won / row.total) * 100)
              : 0;
            const isExpanded = expanded === row.campaignId;
            return (
              <>
                <tr
                  key={row.campaignId}
                  className="group cursor-pointer transition-colors hover:bg-surface-alt/60"
                  onClick={() => setExpanded(isExpanded ? null : row.campaignId)}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <ChevronDown
                        size={13}
                        className={clsx(
                          'shrink-0 text-text-muted transition-transform',
                          isExpanded && 'rotate-180',
                        )}
                      />
                      <div>
                        <div className="font-semibold text-text-primary text-[13px]">
                          {row.campaignName}
                        </div>
                        <div className="font-mono text-[10px] text-text-muted">{row.campaignId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-text-primary">
                    {fmtNum(row.total)}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums text-text-secondary">
                    {fmtNum(row.byStatus.qualified)}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="inline-flex items-center gap-1 tabular-nums text-success font-semibold">
                      <Trophy size={11} />
                      {fmtNum(row.byStatus.won)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={clsx(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      winRate >= 20
                        ? 'bg-success-soft text-success'
                        : winRate >= 10
                        ? 'bg-warning-soft text-warning'
                        : 'bg-surface-alt text-text-muted',
                    )}>
                      {winRate}%
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right tabular-nums text-text-primary font-semibold">
                    {row.dealValue > 0 ? fmtCurrency(row.dealValue, companyCurrency) : '—'}
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${row.campaignId}-actions`}>
                    <td colSpan={6} className="px-5 py-3 bg-primary-soft/20 border-b border-primary/10">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-5 text-xs text-text-secondary">
                          {Object.entries(row.byStatus).map(([s, n]) => (
                            <span key={s} className="flex items-center gap-1">
                              <span className="capitalize font-medium text-text-primary">{s}:</span>
                              {fmtNum(n)}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads?sourceCampaignId=${row.campaignId}`);
                          }}
                          className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          View leads <ArrowUpRight size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeadsAnalytics() {
  const companyCurrency = useCompanyCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePreset, setActivePreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const [funnel, setFunnel] = useState<FunnelResult | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[] | null>(null);
  const [timings, setTimings] = useState<TimingsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [source, setSource] = useState('');

  const campaignFilter = searchParams.get('campaign') ?? undefined;

  const getFilter = useCallback(() => {
    const sourceFilter = source || undefined;
    if (activePreset === 'All time') return { source: sourceFilter };
    const preset = PRESETS.find((p) => p.label === activePreset);
    if (preset) return { from: preset.from, to: preset.to, campaignId: campaignFilter, source: sourceFilter };
    // Custom
    return {
      from: customFrom || undefined,
      to: customTo || undefined,
      campaignId: campaignFilter,
      source: sourceFilter,
    };
  }, [activePreset, customFrom, customTo, campaignFilter, source]);

  const load = useCallback(async () => {
    setLoading(true);
    const filter = getFilter();
    try {
      const [f, c, t] = await Promise.all([
        leadsAnalyticsService.getFunnel(filter),
        leadsAnalyticsService.getByCampaign(filter),
        leadsAnalyticsService.getTimings(filter),
      ]);
      setFunnel(f);
      setCampaigns(c);
      setTimings(t);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [getFilter]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const filter = getFilter();
      await leadsAnalyticsService.exportCsv(filter);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const qualifiedRate = funnel && funnel.total > 0
    ? Math.round((funnel.qualified / funnel.total) * 100)
    : 0;

  return (
    <>
      <Topbar
        title="Lead analytics"
        breadcrumb="Leads / Analytics"
        description="How your campaigns convert."
        right={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Download size={13} />}
              loading={exporting}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-6">
        {/* Date range picker */}
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => { setActivePreset(p.label); setShowCustom(false); }}
              className={clsx(
                'rounded-full border px-3.5 py-1.5 text-xs font-semibold transition',
                activePreset === p.label && !showCustom
                  ? 'border-primary bg-primary-soft text-primary'
                  : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => { setShowCustom(true); setActivePreset(''); }}
            className={clsx(
              'rounded-full border px-3.5 py-1.5 text-xs font-semibold transition',
              showCustom
                ? 'border-primary bg-primary-soft text-primary'
                : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
            )}
          >
            Custom
          </button>
          {showCustom && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
              />
              <span className="text-xs text-text-muted">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
              />
              <Button size="sm" variant="primary" onClick={load}>Apply</Button>
            </div>
          )}

          {/* Source / channel scope */}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-muted">Channel</span>
            <Select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
              title="Filter analytics by source channel"
            >
              <option value="">All</option>
              <option value="ctwa">📘 Meta ad (CTWA)</option>
              <option value="lead_ad_form">📋 Meta lead form</option>
              <option value="tiktok_lead_ad">🎵 TikTok lead form</option>
              <option value="google_lead_form">🔎 Google lead form</option>
              <option value="google_ads">🔎 Google ad (CTWA)</option>
              <option value="web_form">🌐 Web form</option>
              <option value="web_chat">💬 Website chat</option>
              <option value="chat_api">🔌 App chat</option>
              <option value="email">✉️ Email</option>
              <option value="inbound_keyword">💬 WhatsApp inbound</option>
              <option value="manual">✋ Manual</option>
            </Select>
          </div>
        </div>

        {/* KPI tiles */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label="Total leads"
              value={fmtNum(funnel?.total ?? 0)}
              icon={<BarChart2 size={16} />}
              accent="blue"
            />
            <StatTile
              label="Qualified rate"
              value={`${qualifiedRate}%`}
              icon={<Target size={16} />}
              accent="amber"
            />
            <StatTile
              label="Won deals"
              value={fmtNum(funnel?.won ?? 0)}
              icon={<Trophy size={16} />}
              accent="green"
            />
            <StatTile
              label="Avg time-to-qualified"
              value={formatDuration(timings?.medianTimeToQualifiedMs ?? null)}
              icon={<Clock size={16} />}
              accent="gold"
            />
          </div>
        )}

        {/* Funnel + Timings */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {/* Funnel */}
          <Card padded>
            <SectionHeader title="Conversion funnel" icon={<BarChart2 size={15} />} />
            {loading || !funnel ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-6 rounded-lg" />)}
              </div>
            ) : funnel.total === 0 ? (
              <p className="text-xs text-text-muted italic py-4">No leads in this period.</p>
            ) : (
              <FunnelBars funnel={funnel} />
            )}
          </Card>

          {/* Timing metrics */}
          <Card padded>
            <SectionHeader title="Response timings" icon={<Clock size={15} />} />
            {loading || !timings ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-8 rounded-lg" />)}
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  { label: 'Median time to first reply', value: timings.medianTimeToFirstReplyMs },
                  { label: 'P90 time to first reply', value: timings.p90TimeToFirstReplyMs },
                  { label: 'Median time to qualified', value: timings.medianTimeToQualifiedMs },
                  { label: 'Median time to won', value: timings.medianTimeToWonMs },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between rounded-xl border border-border bg-surface-alt px-4 py-2.5">
                    <span className="text-xs text-text-secondary">{label}</span>
                    <span className="text-sm font-bold tabular-nums text-text-primary">
                      {formatDuration(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* By campaign table */}
        <Card padded={false} className="overflow-hidden">
          <div className="px-5 pt-5 pb-3">
            <SectionHeader
              title="By campaign"
              subtitle={campaigns ? `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}` : undefined}
              icon={<BarChart2 size={15} />}
            />
          </div>
          {loading || !campaigns ? (
            <div className="space-y-2 p-5 pt-0">
              {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
            </div>
          ) : (
            <CampaignTable rows={campaigns} />
          )}
        </Card>

        {/* Mini recharts bar — deal value by campaign */}
        {!loading && campaigns && campaigns.filter((c) => c.dealValue > 0).length > 0 && (
          <Card padded>
            <SectionHeader title="Deal value by campaign" icon={<Trophy size={15} />} />
            <div className="mt-3 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={campaigns
                    .filter((c) => c.dealValue > 0)
                    .sort((a, b) => b.dealValue - a.dealValue)
                    .slice(0, 8)
                    .map((c) => ({ name: c.campaignName.slice(0, 20), value: c.dealValue }))}
                  margin={{ top: 4, right: 8, bottom: 4, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: 'var(--color-text-muted, #9ca3af)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--color-text-muted, #9ca3af)' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    formatter={(v: unknown) => [fmtCurrency(Number(v), companyCurrency), 'Deal value']}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {campaigns
                      .filter((c) => c.dealValue > 0)
                      .slice(0, 8)
                      .map((_, idx) => (
                        <Cell
                          key={idx}
                          fill={`hsl(${220 + idx * 25}, 80%, ${55 + idx * 3}%)`}
                        />
                      ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
